<template>
  <div
    class="ubbi-select"
    :class="{ 'is-open': isOpen, 'is-disabled': disabled }"
    ref="root"
  >
    <button
      type="button"
      :class="buttonClass"
      v-bind="$attrs"
      :disabled="disabled"
      :id="id"
      @click.stop="toggle"
    >
      <span class="ubbi-select__value" :class="{ 'is-placeholder': !hasValue }">
        <slot name="value" :option="selectedOption">{{ displayLabel }}</slot>
      </span>
      <i class="bi" :class="isOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
    </button>
    <Teleport to="body">
      <div
        v-if="isOpen"
        class="ubbi-select__panel"
        :style="panelStyle"
        @click.stop
      >
        <input
          v-if="searchable"
          ref="search"
          v-model="query"
          type="text"
          class="ubbi-select__search"
          placeholder="Buscar..."
        >
        <button
          v-if="showClear && hasValue"
          type="button"
          class="ubbi-select__option is-clear"
          @click="clear"
        >
          Limpar
        </button>
        <ul class="ubbi-select__list">
          <li v-for="option in filtered" :key="String(optionKey(option))">
            <button
              type="button"
              class="ubbi-select__option"
              :class="{ 'is-on': isSelected(option) }"
              @click="pick(option)"
            >
              <span v-if="multiple" class="ubbi-select__check" :class="{ 'is-on': isSelected(option) }">
                <i v-if="isSelected(option)" class="bi bi-check-lg"></i>
              </span>
              <slot name="option" :option="option">
                <span
                  v-if="option.color"
                  class="ubbi-select__chip"
                  :style="{ background: option.color }"
                >{{ optionText(option) }}</span>
                <span v-else>{{ optionText(option) }}</span>
              </slot>
            </button>
          </li>
        </ul>
        <p v-if="!filtered.length" class="ubbi-select__empty">Nenhuma opção encontrada</p>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'SelectUbbi',
  inheritAttrs: false,
  props: {
    modelValue: { default: null },
    options: { type: Array, default: () => [] },
    optionLabel: { type: String, default: 'label' },
    optionValue: { type: String, default: 'value' },
    filterFields: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Selecione' },
    searchable: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    showClear: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    size: { type: String, default: 'md' },
    id: { type: String, default: '' },
    open: { default: null },
  },
  emits: ['update:modelValue', 'update:open', 'change', 'search'],
  data() {
    return {
      localOpen: false,
      query: '',
      panelStyle: {
        opacity: 0,
        pointerEvents: 'none',
      },
    };
  },
  computed: {
    isOpen() {
      return this.open == null ? this.localOpen : Boolean(this.open);
    },
    buttonClass() {
      return [
        'ubbi-select__btn',
        'form-control',
        'form-control-solid',
        this.size === 'sm' ? 'form-control-sm' : null,
        this.$attrs.class,
      ];
    },
    selectedValues() {
      if (!this.multiple) return [];
      if (!this.modelValue) return [];
      return String(this.modelValue).split(',').map((item) => item.trim()).filter(Boolean);
    },
    hasValue() {
      if (this.multiple) return this.selectedValues.length > 0;
      return this.modelValue != null && this.modelValue !== '';
    },
    selectedOption() {
      if (this.multiple || !this.hasValue) return null;
      return this.options.find((option) => String(this.optionKey(option)) === String(this.modelValue)) || null;
    },
    displayLabel() {
      if (this.multiple) {
        return this.selectedValues.length
          ? `${this.selectedValues.length} item(s) selecionado(s)`
          : this.placeholder;
      }
      return this.selectedOption ? this.optionText(this.selectedOption) : this.placeholder;
    },
    filtered() {
      const query = this.query.trim().toLowerCase();
      if (!query) return this.options;
      return this.options.filter((option) => this.searchText(option).includes(query));
    },
  },
  watch: {
    query(value) {
      this.$emit('search', value);
    },
    isOpen(value) {
      this.query = '';
      if (value) {
        this.$nextTick(() => {
          this.placePanel();
          requestAnimationFrame(() => {
            this.$refs.search?.focus?.({ preventScroll: true });
          });
        });
      }
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentDown, true);
    window.addEventListener('resize', this.close);
    window.addEventListener('scroll', this.onScroll, true);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onDocumentDown, true);
    window.removeEventListener('resize', this.close);
    window.removeEventListener('scroll', this.onScroll, true);
  },
  methods: {
    optionText(option) {
      return String(option?.[this.optionLabel] ?? '');
    },
    optionKey(option) {
      return option?.[this.optionValue];
    },
    searchText(option) {
      const fields = this.filterFields.length ? this.filterFields : [this.optionLabel];
      return fields.map((field) => String(option?.[field] ?? '')).join(' ').toLowerCase();
    },
    isSelected(option) {
      const key = this.optionKey(option);
      if (this.multiple) return this.selectedValues.some((value) => String(value) === String(key));
      return String(this.modelValue) === String(key);
    },
    setOpen(value) {
      this.localOpen = value;
      this.$emit('update:open', value);
    },
    toggle() {
      if (this.disabled) return;
      this.setOpen(!this.isOpen);
    },
    close() {
      if (!this.isOpen) return;
      this.setOpen(false);
    },
    onDocumentDown(event) {
      if (!this.isOpen) return;
      const target = event.target;
      if (this.$refs.root?.contains(target)) return;
      if (target?.closest?.('.ubbi-select__panel')) return;
      this.close();
    },
    onScroll(event) {
      if (!this.isOpen) return;
      if (event.target?.closest?.('.ubbi-select__panel')) return;
      this.placePanel();
    },
    placePanel() {
      const root = this.$refs.root;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 220 && rect.top > spaceBelow;
      this.panelStyle = {
        left: `${Math.max(8, rect.left)}px`,
        width: `${Math.max(rect.width, 180)}px`,
        zIndex: 2300,
        top: openUp ? 'auto' : `${rect.bottom + 6}px`,
        bottom: openUp ? `${window.innerHeight - rect.top + 6}px` : 'auto',
        opacity: 1,
        pointerEvents: 'auto',
      };
    },
    emitValue(value) {
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
    },
    pick(option) {
      const key = this.optionKey(option);
      if (this.multiple) {
        const next = this.isSelected(option)
          ? this.selectedValues.filter((value) => String(value) !== String(key))
          : [...this.selectedValues, key];
        this.emitValue(next.join(','));
        return;
      }
      this.emitValue(key);
      this.close();
    },
    clear() {
      this.emitValue(this.multiple ? '' : null);
      this.close();
    },
  },
};
</script>

<style scoped>
.ubbi-select {
  position: relative;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}
.ubbi-select.is-disabled {
  opacity: 0.65;
  pointer-events: none;
}
.ubbi-select__btn {
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-align: left;
  cursor: pointer;
}
.ubbi-select__btn.form-control {
  display: flex;
  overflow: hidden;
}
.ubbi-select.is-open .ubbi-select__btn,
.ubbi-select__btn:hover,
.ubbi-select__btn:focus {
  border-color: #780f5a;
  box-shadow: none;
}
.ubbi-select__value {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ubbi-select__value.is-placeholder {
  color: #a1a5b7;
}
.ubbi-select__btn i {
  flex-shrink: 0;
  font-size: 0.85rem;
  color: #7e8299;
}
</style>

<style>
.ubbi-select__panel {
  position: fixed !important;
  max-height: 16rem;
  overflow: auto;
  padding: 0.35rem;
  background: #fff;
  border: 1px solid #e4e6ef;
  border-radius: 0.55rem;
  box-shadow: 0 12px 32px rgba(24, 28, 50, 0.16);
}
.ubbi-select__search {
  width: 100%;
  margin-bottom: 0.3rem;
  padding: 0.4rem 0.5rem;
  border: 1px solid #e4e6ef;
  border-radius: 0.4rem;
  background: #fff;
  font-size: 0.8rem;
}
.ubbi-select__search:focus {
  outline: none;
  border-color: #780f5a;
}
.ubbi-select__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.ubbi-select__option {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  width: 100%;
  min-width: 0;
  padding: 0.42rem 0.45rem;
  border: 0;
  border-radius: 0.4rem;
  background: transparent;
  color: #181c32;
  font-size: 0.84rem;
  font-weight: 600;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
}
.ubbi-select__option:hover,
.ubbi-select__option.is-on {
  background: #f8f5f7;
}
.ubbi-select__option.is-clear {
  color: #f1416c;
}
.ubbi-select__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 0.95rem;
  height: 0.95rem;
  border: 1px solid #c4cada;
  border-radius: 0.22rem;
  background: #fff;
  color: #fff;
  flex-shrink: 0;
}
.ubbi-select__check.is-on {
  border-color: #780f5a;
  background: #780f5a;
}
.ubbi-select__check i {
  font-size: 0.62rem;
  line-height: 1;
}
.ubbi-select__chip {
  display: inline-flex;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
}
.ubbi-select__empty {
  margin: 0.35rem 0.2rem;
  color: #a1a5b7;
  font-size: 0.72rem;
  font-weight: 600;
  text-align: center;
}
</style>
